import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Map as MapIcon, PenTool, Layers, Sparkles, ChevronRight } from "lucide-react";
import SeoHead from "../components/SeoHead";

/**
 * SEO landing page for Cartomancer.
 *
 * Public, indexable marketing page. The /app route (the actual editor) remains
 * gated by ownership of Cartomancer Basic/Premium; this page explains the
 * product to prospects and links to the store + the editor.
 */
export default function CartomancerLanding() {
  const { t } = useTranslation();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Cartomancer",
    applicationCategory: "DesignApplication",
    operatingSystem: "Web",
    description: t("landing.carto.seoDesc"),
    url: "https://mytharion.app/cartomancer",
    offers: [
      {
        "@type": "Offer",
        name: "Cartomancer Basic",
        price: "7.99",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: "https://mytharion.app/product/cartomancer_basic",
      },
      {
        "@type": "Offer",
        name: "Cartomancer Premium",
        price: "2.99",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: "https://mytharion.app/product/cartomancer_premium",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "2.99",
          priceCurrency: "EUR",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
        },
      },
    ],
    publisher: { "@type": "Organization", name: "Mytharion", url: "https://mytharion.app/" },
  };

  const Feature = ({ icon: Icon, title, desc }) => (
    <div className="rune-border p-5 bg-card/60 rounded-lg">
      <Icon className="w-6 h-6 text-gold mb-3" />
      <h3 className="font-heading text-base text-parchment mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </div>
  );

  return (
    <>
      <SeoHead
        title={t("landing.carto.seoTitle")}
        description={t("landing.carto.seoDesc")}
        path="/cartomancer"
        jsonLd={jsonLd}
      />
      <div className="container py-16 space-y-16">
        <header className="max-w-3xl">
          <p className="text-xs font-heading uppercase tracking-widest text-arcane mb-3">
            {t("landing.eyebrow")} · {t("landing.carto.eyebrowSub")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-gold mb-4">
            {t("landing.carto.h1")}
          </h1>
          <p className="text-base text-parchment/90 mb-6 leading-relaxed">
            {t("landing.carto.lead")}
            <Link to="/mytharion-vtt" className="text-gold hover:underline ml-1">
              MytharionVTT
            </Link>
            .
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/product/cartomancer_basic"
              data-testid="carto-landing-cta-buy-basic"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-gradient-to-b from-yellow-600 to-yellow-800 text-black font-heading gold-glow"
            >
              {t("landing.carto.ctaBuyBasic")} <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/product/cartomancer_premium"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md rune-border text-gold hover:bg-yellow-900/20 font-heading"
            >
              {t("landing.carto.ctaTryPremium")}
            </Link>
          </div>
        </header>

        <section aria-labelledby="carto-features">
          <h2 id="carto-features" className="font-display text-3xl text-gold mb-6">
            {t("landing.carto.featuresTitle")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Feature icon={MapIcon} title={t("landing.carto.f1Title")} desc={t("landing.carto.f1Desc")} />
            <Feature icon={PenTool} title={t("landing.carto.f2Title")} desc={t("landing.carto.f2Desc")} />
            <Feature icon={Layers} title={t("landing.carto.f3Title")} desc={t("landing.carto.f3Desc")} />
            <Feature icon={Sparkles} title={t("landing.carto.f4Title")} desc={t("landing.carto.f4Desc")} />
          </div>
        </section>

        <section aria-labelledby="carto-plans">
          <h2 id="carto-plans" className="font-display text-3xl text-gold mb-6">
            {t("landing.carto.plansTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <article className="p-6 rune-border bg-card/60 rounded-lg">
              <h3 className="font-heading text-lg text-parchment mb-2">
                {t("landing.carto.basicTitle")}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t("landing.carto.basicDesc")}
              </p>
              <Link to="/product/cartomancer_basic" className="text-gold hover:underline text-sm">
                {t("landing.carto.seeProduct")}
              </Link>
            </article>
            <article className="p-6 rune-border bg-card/60 rounded-lg">
              <h3 className="font-heading text-lg text-parchment mb-2">
                {t("landing.carto.premiumTitle")}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t("landing.carto.premiumDesc")}
              </p>
              <Link to="/product/cartomancer_premium" className="text-gold hover:underline text-sm">
                {t("landing.carto.seeProduct")}
              </Link>
            </article>
          </div>
        </section>
      </div>
    </>
  );
}
